import { ENUM_VIEW_MODE } from '@enums-global/enum-view-mode';
import { CoveredEntityServiceType } from '@module-covered-entity/enums/ce-account.enum';
import { AuditCustomerType } from '@module-customer/enums/audit-customer-type.enum';
import { CustomerServiceType } from '@module-customer/enums/customer-service-type.enum';
import { CE_ROLES, ENUM_ROLES } from '@module-role/enums/enum-roles';
import { ENUM_USER_TYPE } from '@module-role/enums/enum-user-type';
import { RoleTypeData } from '@module-role/interfaces/role-type-data.interface';
import { ModelUser } from '@module-user/models/model-user';
import { Values } from '@utils-global/type-values';

export function isAccess(user: ModelUser, config: RoleTypeData): boolean {
  const hasRole =
    config?.roles === undefined ||
    config.roles.some((r) => user.roles.some((x) => x === r));
  const hasPermission =
    config?.permissions === undefined ||
    config.permissions.some((r) => user.permissions.some((x) => x === r));

  return hasRole && hasPermission;
}

export function getViewMode(user: ModelUser): Values<typeof ENUM_VIEW_MODE> {
  if (
    isAccess(user, {
      roles: [
        ENUM_ROLES.RXP_ADMIN,
        ENUM_ROLES.RXP_EXECUTIVE,
        ENUM_ROLES.RXP_STAFF
      ]
    })
  ) {
    return ENUM_VIEW_MODE.RXP_VIEW;
  }

  if (
    isAccess(user, {
      roles: [
        ENUM_ROLES.MFG_ADMIN,
        ENUM_ROLES.MFG_EXECUTIVE,
        ENUM_ROLES.MFG_STAFF
      ]
    })
  ) {
    return ENUM_VIEW_MODE.MANUFACTURER_VIEW;
  }

  return ENUM_VIEW_MODE.CUSTOMER_VIEW;
}

export function isAdminUser(user: ModelUser): boolean {
  return isAccess(user, {
    roles: [ENUM_ROLES.ADMIN]
  });
}

export function isRxpUser(user: ModelUser): boolean {
  return isAccess(user, {
    roles: [
      ENUM_ROLES.RXP_ADMIN,
      ENUM_ROLES.RXP_EXECUTIVE,
      ENUM_ROLES.RXP_STAFF
    ]
  });
}

export function isRxpAdminUser(user: ModelUser): boolean {
  return isAccess(user, {
    roles: [ENUM_ROLES.RXP_ADMIN]
  });
}

export function isCustomerUser(user: ModelUser): boolean {
  return isAccess(user, {
    roles: [
      ENUM_ROLES.CUSTOMER_ADMIN,
      ENUM_ROLES.CUSTOMER_EXECUTIVE,
      ENUM_ROLES.CUSTOMER_STAFF,
      ENUM_ROLES.CUSTOMER_REPORTER
    ]
  });
}

export function isAuditCustomerUser(
  user: ModelUser,
  auditCustomerTypes?: AuditCustomerType[]
): boolean {
  return (
    isCustomerUser(user) &&
    user.customers[0]?.serviceType === CustomerServiceType.AUDIT_340B &&
    (!auditCustomerTypes ||
      auditCustomerTypes.includes(user.customers[0]?.auditCustomerType!))
  );
}

export function isRebateCustomerUser(user: ModelUser): boolean {
  return (
    isCustomerUser(user) &&
    user.customers[0]?.serviceType === CustomerServiceType.REBATE_OPERATIONS
  );
}

export function isHealthSystemCustomerUser(user: ModelUser): boolean {
  return (
    isCustomerUser(user) &&
    user.customers[0]?.serviceType === CustomerServiceType.HEALTH_SYSTEM
  );
}

export function isPharmacyChainCustomerUser(user: ModelUser): boolean {
  return (
    isCustomerUser(user) &&
    user.customers[0]?.serviceType === CustomerServiceType.PHARMACY_CHAIN
  );
}

export function isCustomerReporterUser(user: ModelUser): boolean {
  return isAccess(user, {
    roles: [ENUM_ROLES.CUSTOMER_REPORTER]
  });
}

export function isManufacturerUser(user: ModelUser): boolean {
  return isAccess(user, {
    roles: [
      ENUM_ROLES.MFG_ADMIN,
      ENUM_ROLES.MFG_EXECUTIVE,
      ENUM_ROLES.MFG_STAFF
    ]
  });
}

export function isCEUser(user: ModelUser): boolean {
  return isAccess(user, {
    roles: [ENUM_ROLES.CE_ADMIN, ENUM_ROLES.CE_STAFF]
  });
}

export function isCEAdminUser(user: ModelUser): boolean {
  return isAccess(user, {
    roles: [ENUM_ROLES.CE_ADMIN]
  });
}

export function isCEUserWithMFPRebate(user: ModelUser): boolean {
  return (
    isAccess(user, {
      roles: [...CE_ROLES]
    }) &&
    !!user.coveredentities[0]?.serviceTypeConfigurations?.some((cfg) =>
      [CoveredEntityServiceType.MFP, CoveredEntityServiceType.REBATE].includes(
        cfg.ceServiceType.id
      )
    )
  );
}

export function isTPAUser(user: ModelUser): boolean {
  return isAccess(user, {
    roles: [ENUM_ROLES.TPA_ADMIN, ENUM_ROLES.TPA_STAFF]
  });
}

export function isPharmacyUser(user: ModelUser): boolean {
  return isAccess(user, {
    roles: [ENUM_ROLES.PHARMACY_ADMIN]
  });
}

/**
 * Account-scoped screens (Financial Dashboard, Reports) use Pharmacy Admin
 * context when that role is present. A selected CE from CE Details always
 * takes precedence over the user's broader roles.
 */
export function usePharmacyAccountContext(
  user: ModelUser,
  selectedCe?: { id?: string | null; accountId?: string | null }
): boolean {
  if (selectedCe?.id && selectedCe?.accountId) {
    return false;
  }
  return isPharmacyUser(user);
}

export function getUserType(user: ModelUser): Values<typeof ENUM_USER_TYPE> {
  if (isAdminUser(user)) {
    return ENUM_USER_TYPE.ADMIN;
  }

  if (isRxpUser(user)) {
    return ENUM_USER_TYPE.RXP;
  }

  if (isCustomerUser(user)) {
    return ENUM_USER_TYPE.CUSTOMER;
  }

  if (isManufacturerUser(user)) {
    return ENUM_USER_TYPE.MFG;
  }

  if (isCEUser(user)) {
    return ENUM_USER_TYPE.CE;
  }

  if (isTPAUser(user)) {
    return ENUM_USER_TYPE.TPA;
  }

  if (isPharmacyUser(user)) {
    return ENUM_USER_TYPE.PHARMACY;
  }

  return ENUM_USER_TYPE.UNKNOWN;
}
